import { Alert, Card } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button  } from "./Controls/Button";
import { useEffect, useState } from "react";
import { Alphabet, Asterisk, At, Telephone } from "react-bootstrap-icons";
import { CenteredPage } from "./Controls/CenteredPage";
import { Input } from "./Controls/Input";
import { HeaderText } from "./Controls/HeaderText";
import { Api } from "./Data/Api";
import { useAtom, useSetAtom } from "jotai";
import { actingAsAtom, authAtom, authTokenAtom, redirectAtom } from "./Data/Atoms";
import { parseParker } from "./Data/ApiParse";
import { PhoneInput } from "./Controls/PhoneInput";
import { validatePassword } from "./Data/Validation";
import { toast } from "react-toastify";
import { loginAndRedirect } from "./Data/AuthUtils";
import { clearLocalStoragePreserving, STORAGE_GROUPS } from "./shared/localStorageHelper";
import { axiosClient } from "./shared/apiConfig";

export const SignUpPage = () => {
    const [auth,   setAuth] = useAtom( authAtom );
    const nav = useNavigate();
    const [errMsg, setErrMsg] = useState<string>();
    const [redirect, setRedirect] = useAtom( redirectAtom );
    const [isLoading, setIsLoading] = useState(false);
    const [authToken, setAuthToken] = useAtom(authTokenAtom);
    const setActingAs = useSetAtom(actingAsAtom);
    const [searchParams] = useSearchParams();
    const delegateInviteCode = searchParams.get("delegate-invite");
    const [inviteEmail, setInviteEmail] = useState<string | null>(null);
    const [inviteOwner, setInviteOwner] = useState<string | null>(null);
    const [inviteOwnerParkerId, setInviteOwnerParkerId] = useState<number | null>(null);
    
    interface SignUpForm {
        name:            string;
        emailAddress:    string;
        phoneNumber:     string;
        password:        string;
        passwordConfirm: string;
    }

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
        setError,
    } = useForm<SignUpForm>();

    useEffect(() => {
        if (!delegateInviteCode) return;
        axiosClient.get(`/connect/delegate/invite-info/${delegateInviteCode}`)
            .then(res => {
                const { email, ownerName, ownerParkerId } = res.data;
                setInviteEmail(email);
                setInviteOwner(ownerName);
                setInviteOwnerParkerId(ownerParkerId);
                setValue("emailAddress", email);
            })
            .catch(() => {});
    }, [delegateInviteCode, setValue]);

    const password = watch( "password" );
    function onSubmit( data: SignUpForm ) {
        const validation = validatePassword(data.password);
        if (!validation.isValid) {
           
            validation.errors.forEach(error => {
                setError('password', { type: 'manual', message: error });
            });
            return;
        }

        // Clear localStorage BEFORE making any API calls (preserve help-dialog preferences)
        clearLocalStoragePreserving([STORAGE_GROUPS.helpDialogs]);
        
        setIsLoading( true );
        Api.signUp( { ...data } )
            .then( res => {
                if( res.isOk ) {
                    const parker = parseParker(res.value);
                    toast.success("Account created succesfully!");
                    
                    return loginAndRedirect({
                        emailAddress: data.emailAddress,
                        password: data.password,
                        parker,
                        setAuthToken,
                        setAuth,
                        redirect: delegateInviteCode ? "/user/plan" : redirect,
                        setRedirect,
                        nav,
                        beforeNav: (delegateInviteCode && inviteOwnerParkerId && inviteOwner)
                            ? () => setActingAs({ parkerId: inviteOwnerParkerId, name: inviteOwner })
                            : undefined,
                    });
                }
                setErrMsg( res.error );
            } ).finally(() => {
                setIsLoading( false );
            } );
    }

    return <CenteredPage sm="10" md="8" lg="6" xl="4" style={{ maxWidth: "30em" }}>
        <Card className="w-100">
            <form onSubmit={handleSubmit( onSubmit )}>
                <Card.Header>
                    <HeaderText>Sign Up</HeaderText>
                </Card.Header>
                <Card.Body>
                    {inviteOwner && (
                        <Alert variant="info" className="mb-3">
                            <strong>{inviteOwner}</strong> invited you to access their parking account.
                        </Alert>
                    )}
                    <Input
                        type="text"
                        label="Name"
                        icon={<Alphabet className="fs-5" />}
                        placeholder="Enter your full name"
                        error={errors.name}
                        autoFocus
                        {...register( "name", { required: "Name is required" } )}
                    />
                    
                    <Input
                        type="email"
                        label="Email Address"
                        icon={<At className="fs-5" />}
                        placeholder="Enter your email address"
                        error={errors.emailAddress}
                        readOnly={!!inviteEmail}
                        inputClassName={inviteEmail ? "bg-light text-muted" : ""}
                        {...register( "emailAddress", { required: "Email Address is required" } )}
                    />

                    <PhoneInput
                        label="Phone Number"
                        icon={<Telephone className="fs-5" />}
                        placeholder="555-555-5555"
                        error={errors.phoneNumber}
                        {...register("phoneNumber", {
                            required: "Phone number is required",
                            pattern: {
                                value: /^\d{3}-\d{3}-\d{4}$/,
                                message: "Phone number must be in format ###-###-####"
                            }
                        })}
                    />
                    <Input
                        type="password"
                        label="Password"
                        icon={<Asterisk />}
                        error={errors.password}
                        {...register( "password", { 
                            required: "Password is required",
                            validate: (value) => {
                                const validation = validatePassword(value);
                                return validation.isValid || validation.errors[0];
                            }
                        } )}
                    />
                    <Input
                        type="password"
                        label="Confirm Password"
                        icon={<Asterisk />}
                        error={errors.passwordConfirm}
                        {...register( "passwordConfirm", {
                            required: "Confirm Password is required",
                            validate: (value) =>
                                value === password || "Passwords do not match",
                        } )}
                    />
                    {errMsg && <Alert variant="danger">{errMsg}</Alert>}
                    <div>
                        <Link to="/login">Already have an account? Log In</Link>
                    </div>
                </Card.Body>
                <Card.Footer>
                    <div>
                        {redirect !== undefined && <Alert>
                            You will be redirected after you log-in.
                        </Alert>}
                    </div>
                    <div className="d-flex flex-row justify-content-end">
                        <Button type="submit" className="btn-main"
                                loading={isLoading}
                                loadingText="Loading">Register</Button>
                    </div>
                </Card.Footer>
            </form>
        </Card>
    </CenteredPage>;
};
